<template>
	<div class="sideways-content-fold" v-if="activePanel">
		<div class="portlet light bordered">
			<div class="portlet-title">
				<div class="caption">
					Edit Business
				</div>
			</div>
			<div class="portlet-body portlet-overflow">
				<form role="form" @submit.prevent="editBusiness($event)" novalidate>
					<div class="alert alert-danger" v-if="errorMessage.length">
					    <button class="close" data-close="alert" @click="clearError()"></button>
                        <span>{{ errorMessage }}</span>
					</div>
					<transition name="fade" mode="out-in">
						<div v-if="process === 'step_1'" key="step_1">
							<div class="form-body invite-user-form">
								<div class="form-group form-md-line-input form-md-floating-label">
		                            <input type="text" class="form-control input-sm" :class="{'edited': editingBusiness.business_name.length}" id="edit_business_name" v-model.trim="editingBusiness.business_name" maxlength="255" @change="businessNameUpdated()">
		                            <label for="edit_business_name">Business Name</label>
		                        </div>
		                    </div>
		                    <div class="form-actions noborder invite-user-actions margin-top-20">
								<button type="button" class="btn btn-default exit-invitation-setup" @click="closePanel()"> Return To Business Dashboard</button>
								<button type="button" class="btn blue" @click="advanceForm($event, 'step_2', 'step_1')" key="edit-business-continue-one">Continue <i class="fa fa-arrow-circle-right"></i></button>
							</div>
						</div>
						<div v-if="process === 'step_2'" key="step_2">
							<div class="form-body invite-user-form">
								<div class="margin-top-20 portlet-title-alternate">Edit Product Package</div>
								<div class="margin-top-20 row search-content-4">
			      					<div class="search-table table-responsive">
			      						<table class="table table-striped ta ble-condensed">
			      							<thead>
			      								<tr>
			      									<th></th>
			      									<th><a class="no-click font-grey-mint">Include App</a></th>
			      								</tr>
			      							</thead>
			      							<tbody>
			      								<tr v-for="app in apps">
				      								<td><h4>{{ app.app_name }}</h4></td>
				      								<td>
				      									<div class="md-checkbox">
				      										<input type="checkbox" :id="`app_access_business_${app.app_id}`" class="md-check" v-model="app.business_access" @change="forceAppSelection('business_access', app)">	
				      										<label :for="`app_access_business_${app.app_id}`">
				      											<span></span>
				      											<span class="check"></span>
				      											<span class="box"></span>
				      										</label>
				      									</div>
				      								</td>
				      							</tr>
				      						</tbody>
			      						</table>
			      					</div>
			      				</div>
							</div>
							<div class="form-actions noborder invite-user-actions">
								<button type="button" class="btn btn-default exit-invitation-setup" @click="closePanel()"> Return To Business Dashboard</button>
								<button type="button" class="btn btn-default" @click="reverseForm()">Back</button>
								<button type="submit" class="btn blue" key="edit-business-submit">Submit</button>
							</div>
						</div>
					</transition>
					<step-counter :steps="steps" :process="process"></step-counter>
				</form>
			</div>
		</div>
	</div>
</template>
<script>
import StepCounter from '../../modules/StepCounter'
import GlobalFunctions from '../../../global'
import App from '../../../controllers/App'
import Business from '../../../controllers/Business'
import { findIndex, isEmpty } from 'lodash'

/**
 * Edit Business module is a page for editing the name and apps of a business.
 * @class EditBusiness
 * @extends BusinessManager
 * @since 0.0.9
 */

export default {
	name: 'edit-business',
	data () {
		return {
			activePanel: false,
			errorMessage: '',
			process: 'step_1',
			businessId: 0,
			editingBusiness: {
				business_name: ''
			},
			steps: [{key: 'step_1', name: 'Business Info'}, {key: 'step_2', name: 'Product Package'}],
			apps: [],
			updatedValues: {
				business_name: false,
				apps: false
			}
		}
	},
	/**
	 * Run on created to determine if this should be the active panel or not.
	 * @function
	 * @return {undefined}
	 * @memberof EditBusiness
	 * @version 0.0.9
	 */
	created () {
		if (this.$route.name === 'Business Manager - Edit Business') {
			this.activePanel = true
			if (this.$route.params.business_id && this.$route.params.business_id !== 'undefined' && parseInt(this.$route.params.business_id) !== 0) {
				this.businessId = this.$route.params.business_id
				this.getBusinessDetails()
			} else {
				this.closePanel()
			}
		} else {
			this.activePanel = false
		}
	},
	methods: {
		/**
		 * To clear the current error.
		 * @function
		 * @returns {undefined}
		 * @memberof EditBusiness
		 * @version 0.0.9
		 */
		clearError () {
			this.errorMessage = ''
		},
		/**
		 * To close the edit business panel by redirecting to the dashboard
		 * @function
		 * @returns {undefined}
		 * @memberof EditBusiness
		 * @version 0.0.9
		 */
		closePanel () {
			this.$router.push('/app/business_manager')
		},
		/**
		 * To pull in the details of the current business from the backend.
		 * @function
		 * @returns {undefined}
		 * @memberof EditBusiness
		 * @version 0.0.9
		 */
		getBusinessDetails () {
			let _this = this

			Business.getBusinessDetails(this.businessId).then(response => {
				_this.editingBusiness = response.payload
				this.getAppList()
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {}
			}))
		},
		/**
		 * To move to the next step of the form.
		 * @function
		 * @param {object} event - The click event that prompted this function.
		 * @param {string} step - The step that we are moving to.
		 * @param {string} currentStep - The current step.
		 * @returns {object} A promise that will either return an error message or display the success screen.
		 * @memberof EditBusiness
		 * @version 0.0.9
		 */
		advanceForm (event, step, currentStep) {
			let disabledButton = GlobalFunctions.disableButton(event)
			let _this = this

			return this.validateFormData(currentStep).then(response => {
				_this.clearError()
				disabledButton.complete().then(() => {
					_this.process = step
				})
			}).catch(reason => {
				// If validation fails then display the error message
				_this.errorMessage = reason
				disabledButton.cancel()
			})
		},
		/**
		 * To validate that all information on the current step is accurate before allowing the user to proceed.
		 * @function
		 * @param {string} step - The step to be tested.
		 * @returns {object} A promise that will either return an error message or display the success screen.
		 * @memberof EditBusiness
		 * @version 0.0.9
		 */
		validateFormData (step) {
			let _this = this

			return new Promise((resolve, reject) => {
				switch (step) {
				case 'step_1':
					if (_this.editingBusiness.business_name.length < 3) {
						reject('Business name must be at least 3 characters')
					}
					break

				case 'step_2':
					const businessAppsSelected = _this.apps.filter(app => { return app.business_access }).length >= 1

					if (!businessAppsSelected) {
						return _this.$swal({
							title: 'Are you sure?',
							html: 'Do you want to remove all app access from this business?',
							type: 'warning',
							showCancelButton: true,
							confirmButtonText: 'Update Business',
							confirmButtonColor: '#d9534f',
							cancelButtonText: 'Cancel',
							allowOutsideClick: false,
							allowEscapeKey: false,
							allowEnterKey: false,
							showLoaderOnConfirm: true
						}).then(() => {
							resolve()
						}, dismiss => {
							reject()
						})
					}
					break

				default:
					resolve()
				}

				resolve()
			})
		},
		/**
		 * To move the form back to the previous step
		 * @function
		 * @returns {undefined}
		 * @memberof EditBusiness
		 * @version 0.0.9
		 */
		reverseForm () {
			const index = findIndex(this.steps, ['key', this.process])
			if (index) {
				this.process = this.steps[index - 1].key
			}

			return
		},
		/**
		 * To pull in a list of all available apps to be chosen from for the business.
		 * @function
		 * @returns {object} A promise that will either return an error message or display the success screen.
		 * @memberof EditBusiness
		 * @version 0.0.9
		 */
		getAppList () {
			let _this = this

			return App.getApps().then(response => {
				const defaultApps = {business_access: false, location_default: false}
				_this.apps = response.payload.business_apps

				_this.apps.forEach((apps, index, allApps) => {
					allApps[index] = Object.assign({}, apps, defaultApps)

					_this.editingBusiness.apps.forEach(app => {
						if (app.app_id === apps.app_id) {
							allApps[index].business_access = true
						}
					})
				})
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {

					default:
						// No default
					}
				}
			}))
		},
		/**
		 * To force the selection of apps when set as a location default or to deselect the default when turning an app off for the business.
		 * @function
		 * @param {string} type - The type of checkbox that was selected.
		 * @param {object} app - The app that is being updated.
		 * @returns {undefined}
		 * @memberof EditBusiness
		 * @version 0.0.9
		 */
		forceAppSelection (type, app) {
			this.updatedValues.apps = true

			switch (type) {
			case 'business_access':
				if (app.business_access === false) {
					app.location_default = false
				}
				break

			case 'location_default':
				if (app.location_default === true) {
					app.business_access = true
				}
				break

			default:
				// No Default
			}

			return
		},
		/**
		 * To update that the business name has been changed and should be sent as a parameter to the backend
		 * @function
		 * @returns {undefined}
		 * @memberof EditBusiness
		 * @version 0.0.9
		 */
		businessNameUpdated () {
			this.updatedValues.business_name = true
		},
		/**
		 * To filter the selected apps for use with the edit business endpoint
		 * @function
		 * @returns {array} An array of app ids
		 * @memberof EditBusiness
		 * @version 0.0.9
		 */
		formatAppData () {
			let appData = []

			let filteredApps = this.apps.filter(app => app.business_access)

			filteredApps.forEach(app => {
				appData.push(app.app_id)
			})

			return appData
		},
		/**
		 * To send the details of the edited business to the backend.
		 * @function
		 * @param {object} event - The click event that prompted this function.
		 * @returns {object} A promise that will either return an error message or display the success screen.
		 * @memberof EditBusiness
		 * @version 0.0.9
		 */
		editBusiness (event) {
			let disabledButton = GlobalFunctions.disableButton(event)
			let _this = this

			this.editingBusiness.apps = this.formatAppData()

			return _this.validateFormData('step_2')
			.then(response => {
				_this.clearError()
				let params = {}
				if (_this.updatedValues.business_name) {
					params.business_name = _this.editingBusiness.business_name
				}
				if (_this.updatedValues.apps) {
					params.apps = _this.editingBusiness.apps
				}
				if (isEmpty(params)) {
					_this.closePanel()
					return
				}

				Business.updateBusiness(_this.businessId, params).then(response => {
					_this.closePanel()
					disabledButton.complete()
				}).catch(_this.$root.errorWrapper(e => {
					if (e.responseJSON) {
						switch (e.responseJSON.declaration) {
						case 'already_exists':
							_this.errorMessage = 'A business with this name already exists. Please choose a different name.'
							_this.process = 'step_1'
							break

						default:
							// No default
						}
					}
					disabledButton.cancel()
				}))
			}).catch(reason => {
				// If validation fails then display the error message
				if (reason) {
					_this.errorMessage = reason
				}
				disabledButton.cancel()
			})
		},
		/**
		 * To clear the object 'editingBusiness'.
		 * @function
		 * @returns {undefined}
		 * @memberof EditBusiness
		 * @version 0.0.9
		 */
		clearForm () {
			this.editingBusiness = {
				business_name: ''
			}
			this.apps = []
			this.updatedValues = {
				business_name: false,
				apps: false
			}

			this.process = 'step_1'
		}
	},
	components: {
		StepCounter
	},
	watch: {
		/**
		 * To `watch` the $route in order to determine if this panel should be showing or not
		 * @function
		 * @returns {undefined}
		 * @memberof EditBusiness
		 * @version 0.0.9
		 */
		'$route' () {
			if (this.$route.name === 'Business Manager - Edit Business') {
				this.activePanel = true
				this.clearError()
				this.clearForm()

				if (this.$route.params.business_id && this.$route.params.business_id !== 'undefined' && parseInt(this.$route.params.business_id) !== 0) {
					this.businessId = this.$route.params.business_id
					this.getBusinessDetails()
				} else {
					this.closePanel()
				}
			} else {
				this.activePanel = false
			}
		}
	}
}
</script>



// WEBPACK FOOTER //
// EditBusiness.vue?1221b02e